/**
 * One machine: what it is, what happened to it, and the two things that can be done
 * to it.
 *
 * ── The audit trail is read off the resource, and the screen says so ────────
 *
 * The plan's acceptance criterion is *"Audit hiển thị trong detail"*.
 * `GET /api/v1/audit-logs` exists in `openapi.yaml` and is **mounted nowhere** in this
 * build — Task 48 (WP-K8) builds both it and its screen. What `Device` itself carries
 * is the same set of facts `wp_audit_log` records for these transitions:
 * `registered_at`, `approved_at` with `approved_by`, `revoked_at` with
 * `revocation_reason`, and `status_reason` — the free text an administrator gave with
 * the transition that produced the current status. Those are rendered here, and the
 * absence of the full log is stated with the endpoint and the task named, rather than
 * left as a gap a reader has to notice.
 *
 * ── Approve is offered only from `pending`, and that is the server's rule ───
 *
 * `pending` is the only origin: "an `offline` device was moved there by the clock and
 * approving it would be an administrator overwriting a liveness verdict, while
 * `agent_outdated` / `policy_error` / `sync_error` carry a health diagnosis that
 * approval would discard without examining". Any other source state is a `409` whose
 * `details` carry `from` and `allowed_transitions[]`, so this screen does not keep a
 * second copy of the state machine — it offers the control where the server allows it
 * and renders the refusal verbatim when it is wrong.
 *
 * ── What is deliberately not here ────────────────────────────────────────────
 *
 * `PATCH /devices/{id}` — reassignment. It needs an employee directory to pick from;
 * `GET /employees` (Task T2-A) and `EmployeePicker` (Task T2-B) exist now, but this
 * screen has not been rewired to use them for reassignment — a bare id field would
 * answer `404` for "no such employee", "an employee of another company" and "an
 * employee who has left" alike, without being able to say which. The endpoint is
 * named on screen instead. Because this screen issues no `PATCH`, it needs no
 * `If-Match` and reads none.
 */

import { useState, type ReactNode, type SyntheticEvent } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant, formatInstantWithAge } from '../../i18n/datetime';
import type { Device, DeviceHealth } from '../../api/types';
import { isAwaitingApproval, readingOf } from '../../devices/fleet';
import { EmployeePicker, type EmployeePickerValue } from '../../components/EmployeePicker';
import { LivenessBadge, StatusBadge } from './DeviceBadges';
import { DeviceEmployee } from './DeviceTable';
import { RevokeConfirm } from './RevokeConfirm';

function Fact({
  label,
  value,
  testId,
  mono = false,
}: {
  readonly label: string;
  readonly value: string | null;
  readonly testId: string;
  readonly mono?: boolean;
}): ReactNode {
  const { t } = useI18n();
  return (
    <>
      <dt>{label}</dt>
      <dd data-testid={testId} className={mono ? 'wp-mono' : undefined}>
        {/* `??` and not a ternary: eslint's `prefer-nullish-coalescing` is right that
            they are the same expression, and the sentence is the fallback rather than
            a second branch — an absent value is never rendered as a blank cell. */}
        {value ?? <span className="wp-muted">{t('devices.detail.unknown')}</span>}
      </dd>
    </>
  );
}

export interface DeviceDetailProps {
  readonly device: Device;
  readonly health: DeviceHealth | null;
  readonly timezone: string;
  readonly approvePending: boolean;
  readonly revokePending: boolean;
  readonly onApprove: (body: { employee_id?: string; reason?: string }) => void;
  readonly onRevoke: (reason: string) => void;
  /** The panels for the two writes' outcomes, owned by the screen. */
  readonly approveOutcome?: ReactNode;
  readonly revokeOutcome?: ReactNode;
  readonly healthSlot?: ReactNode;
  /** achau employee id → full name, from the directory the screen reads. */
  readonly employeeNames: ReadonlyMap<string, string>;
}

export function DeviceDetail({
  device,
  health,
  timezone,
  approvePending,
  revokePending,
  onApprove,
  onRevoke,
  approveOutcome,
  revokeOutcome,
  healthSlot,
  employeeNames,
}: DeviceDetailProps): ReactNode {
  const { t, locale } = useI18n();
  const [confirming, setConfirming] = useState(false);
  const [approveEmployee, setApproveEmployee] = useState<EmployeePickerValue | null>(null);
  const [approveReason, setApproveReason] = useState('');

  const name = device.hostname ?? device.device_id;
  const reading = readingOf(device.liveness, device.last_heartbeat_at);
  // Terminal in the transition table: `revoked` and `uninstalled` have no outgoing
  // edge, so offering revoke there would offer a `409` with an empty
  // `allowed_transitions[]`.
  const revocable = device.status !== 'revoked' && device.status !== 'uninstalled';

  const submitApprove = (event: SyntheticEvent): void => {
    event.preventDefault();
    const employee = approveEmployee?.achau_user_id ?? '';
    const reason = approveReason.trim();
    onApprove({
      ...(employee === '' ? {} : { employee_id: employee }),
      ...(reason === '' ? {} : { reason }),
    });
  };

  return (
    <section className="wp-section" aria-labelledby="k5-detail-heading" data-testid="k5-detail">
      <h2 className="wp-section__title" id="k5-detail-heading" tabIndex={-1}>
        {name}
      </h2>

      <p className="wp-flags">
        <StatusBadge status={device.status} testId="k5-detail-status" />
        <LivenessBadge reading={reading} testId="k5-detail-liveness" />
      </p>

      {isAwaitingApproval(device.status) ? (
        <p className="wp-small wp-muted" data-testid="k5-detail-pending">
          {t('devices.pending.body')}
        </p>
      ) : null}

      <h3 className="wp-section__title">{t('devices.detail.identity')}</h3>
      <dl className="wp-deflist">
        <Fact
          label={t('devices.table.hostname')}
          value={device.hostname}
          testId="k5-detail-hostname"
          mono
        />
        {/* Not a `Fact`: the same column the table prints, so it reads the same way
            there and here rather than falling back to the bare achau id (R3). */}
        <dt>{t('devices.table.employee')}</dt>
        <dd data-testid="k5-detail-employee">
          <DeviceEmployee employeeId={device.employee_id} names={employeeNames} />
        </dd>
        <Fact
          label={t('devices.detail.os')}
          value={
            device.os_name === null
              ? null
              : [device.os_name, device.os_version, device.os_architecture]
                  .filter((part): part is string => part !== null)
                  .join(' · ')
          }
          testId="k5-detail-os"
        />
        <Fact
          label={t('devices.detail.agentVersion')}
          value={device.agent_version}
          testId="k5-detail-agent"
        />
        <Fact
          label={t('devices.detail.ip')}
          value={device.last_known_ip}
          testId="k5-detail-ip"
          mono
        />
        <Fact
          label={t('devices.detail.department')}
          value={device.department_name}
          testId="k5-detail-department"
        />
        <Fact label={t('devices.detail.branch')} value={device.branch} testId="k5-detail-branch" />
        <Fact
          label={t('devices.detail.policy')}
          value={device.policy_version}
          testId="k5-detail-policy"
        />
        <Fact
          label={t('devices.detail.trust')}
          value={device.trust_status}
          testId="k5-detail-trust"
        />
        <Fact
          label={t('devices.table.heartbeat')}
          value={
            device.last_heartbeat_at === null
              ? null
              : formatInstantWithAge(device.last_heartbeat_at, locale, timezone, t('common.never'))
          }
          testId="k5-detail-heartbeat"
        />
      </dl>

      {healthSlot}

      <h3 className="wp-section__title">{t('devices.detail.audit')}</h3>
      <dl className="wp-deflist" data-testid="k5-detail-audit">
        <Fact
          label={t('devices.detail.registered')}
          value={formatInstant(device.registered_at, locale, timezone)}
          testId="k5-audit-registered"
        />
        <Fact
          label={t('devices.detail.approved')}
          value={
            device.approved_at === null ? null : formatInstant(device.approved_at, locale, timezone)
          }
          testId="k5-audit-approved"
        />
        <Fact
          label={t('devices.detail.approvedBy')}
          value={device.approved_by}
          testId="k5-audit-approved-by"
          mono
        />
        <Fact
          label={t('devices.detail.revoked')}
          value={
            device.revoked_at === null ? null : formatInstant(device.revoked_at, locale, timezone)
          }
          testId="k5-audit-revoked"
        />
        <Fact
          label={t('devices.detail.revokeReason')}
          value={device.revocation_reason}
          testId="k5-audit-revoke-reason"
        />
        <Fact
          label={t('devices.detail.statusReason')}
          value={device.status_reason}
          testId="k5-audit-status-reason"
        />
      </dl>
      <p className="wp-small wp-muted" data-testid="k5-audit-note">
        {t('devices.detail.auditNote')}
      </p>

      {isAwaitingApproval(device.status) ? (
        <form onSubmit={submitApprove} data-testid="k5-approve-form">
          <h3 className="wp-section__title">{t('devices.approve.title')}</h3>
          <p className="wp-small wp-muted">{t('devices.approve.body')}</p>
          {/* The third of the achau-id boxes this screen used to ask a person to type
              (R3). Same picker, same resolution: the list gives the name, the form
              sends `achau_user_id` — the id `admin.ts` resolves in scope. */}
          <div className="wp-field">
            <EmployeePicker
              label={t('devices.approve.employee')}
              testId="k5-approve-employee"
              value={approveEmployee}
              onChange={setApproveEmployee}
            />
            <span className="wp-small wp-muted">{t('devices.employeePickHint')}</span>
          </div>
          <label className="wp-field" htmlFor="k5-approve-reason">
            <span className="wp-field__label">{t('devices.approve.reason')}</span>
            <input
              id="k5-approve-reason"
              className="wp-field__input"
              value={approveReason}
              onChange={(event) => {
                setApproveReason(event.target.value);
              }}
              data-testid="k5-approve-reason"
            />
          </label>
          <button
            type="submit"
            className="wp-button wp-button--primary"
            data-testid="k5-approve-submit"
          >
            {approvePending ? t('devices.approve.submitting') : t('devices.approve.submit')}
          </button>
        </form>
      ) : null}
      {approveOutcome}

      {/* Step one: opens a panel and writes nothing — so it carries danger as **red
          text on paper** and nothing more. The filled red control exists once, inside
          the confirmation, after the five losses have been read and the handle typed
          back (DESIGN.md §3: a solid red button belongs only where the sentence above
          it has already said what will be destroyed). */}
      {revocable && !confirming ? (
        <button
          type="button"
          className="wp-button wp-button--danger"
          onClick={() => {
            setConfirming(true);
          }}
          data-testid="k5-revoke-open"
        >
          {t('devices.revoke.open')}
        </button>
      ) : null}

      {revocable && confirming ? (
        <RevokeConfirm
          device={device}
          health={health}
          pending={revokePending}
          onCancel={() => {
            setConfirming(false);
          }}
          onConfirm={(reason) => {
            setConfirming(false);
            onRevoke(reason);
          }}
        />
      ) : null}
      {revokeOutcome}

      <p className="wp-small wp-muted" data-testid="k5-no-reassign">
        {t('devices.detail.noReassign')}
      </p>
    </section>
  );
}
